/**
 * Configuration centralisée des sources institutionnelles
 * Mapping: source_key → logo → URL officielle
 *
 * Règle SOURCE-UX-001: Logo + Nom + Lien officiel + Date données
 */

// Import des logos SVG
import inrsLogo from '../assets/logos/sources/inrs.svg'
import ansesLogo from '../assets/logos/sources/anses.svg'
import echaLogo from '../assets/logos/sources/echa.svg'
import agritoxLogo from '../assets/logos/sources/agritox.svg'
import pubchemLogo from '../assets/logos/sources/pubchem.svg'

// Date de dernière mise à jour de la base INRS (pas de date par substance)
// Cette date est identique pour toutes les substances INRS
export const INRS_LAST_UPDATE = '2024-01-15'

export type SourceKey = 'inrs' | 'anses' | 'echa' | 'agritox' | 'pubchem'

export interface SourceConfig {
  key: SourceKey
  name: string
  fullName: string
  logo: string
  url: string
  color: string
  bgColor: string
  borderColor: string
  textColor: string
}

/**
 * Configuration de toutes les sources
 * URL = page d'accueil officielle de la source
 */
export const SOURCES_CONFIG: Record<SourceKey, SourceConfig> = {
  inrs: {
    key: 'inrs',
    name: 'INRS',
    fullName: 'Institut National de Recherche et de Sécurité',
    logo: inrsLogo,
    url: 'https://www.inrs.fr',
    color: '#E65100', // Orange INRS (identité actuelle)
    bgColor: 'bg-[#E65100]',
    borderColor: 'border-[#E65100]',
    textColor: 'text-[#E65100]',
  },
  anses: {
    key: 'anses',
    name: 'ANSES',
    fullName: 'Agence Nationale de Sécurité Sanitaire',
    logo: ansesLogo,
    url: 'https://www.anses.fr',
    color: '#00875A',
    bgColor: 'bg-[#00875A]',
    borderColor: 'border-[#00875A]',
    textColor: 'text-[#00875A]',
  },
  echa: {
    key: 'echa',
    name: 'ECHA',
    fullName: 'European Chemicals Agency',
    logo: echaLogo,
    url: 'https://echa.europa.eu',
    color: '#003399',
    bgColor: 'bg-[#003399]',
    borderColor: 'border-[#003399]',
    textColor: 'text-[#003399]',
  },
  agritox: {
    key: 'agritox',
    name: 'AGRITOX',
    fullName: 'Base de données phytopharmaceutiques (INRAE)',
    logo: agritoxLogo,
    url: 'https://www.anses.fr/fr/content/agritox',
    color: '#00A86B', // Vert INRAE (identité institutionnelle)
    bgColor: 'bg-[#00A86B]',
    borderColor: 'border-[#00A86B]',
    textColor: 'text-[#00A86B]',
  },
  pubchem: {
    key: 'pubchem',
    name: 'PubChem',
    fullName: 'NCBI PubChem Database',
    logo: pubchemLogo,
    url: 'https://pubchem.ncbi.nlm.nih.gov',
    color: '#2E86AB',
    bgColor: 'bg-[#2E86AB]',
    borderColor: 'border-[#2E86AB]',
    textColor: 'text-[#2E86AB]',
  },
}

/**
 * Récupère la configuration d'une source par sa clé
 */
export function getSourceConfig(key: string): SourceConfig | undefined {
  return SOURCES_CONFIG[key as SourceKey]
}

/**
 * Liste des clés de sources disponibles
 */
export const SOURCE_KEYS = Object.keys(SOURCES_CONFIG) as SourceKey[]
